/**
 * Story 027.007: the buyer-language labels both routines surfaces and the detail page show for a
 * saved routine. One home, so the chat tree, My routines and the detail cannot disagree. Setup and
 * verification are separate labels: "needs setup" and "not verified yet" are different states.
 * A cost or runtime value that is not available reads as not available, never as zero.
 */
import type {
  PersonalRoutineNeed,
  PersonalRoutineSummary,
  PersonalRoutineUnavailable,
} from "../../hooks/usePersonalRoutines";

export function originLabel(kind: string): string {
  switch (kind) {
    case "authored":
      return "Made by you";
    case "adoption":
      return "From the marketplace";
    case "copy":
      return "Your own copy";
    default:
      return "Shared with you";
  }
}

function needLabel(need: PersonalRoutineNeed): string {
  if (need.kind === "setting") {
    return `the ${need.key ?? "missing"} setting`;
  }
  return `a ${need.domain ?? need.slot ?? "connection"} connection`;
}

export function setupLabel(
  item: Pick<PersonalRoutineSummary, "setupState" | "needsSetup">,
): string {
  if (item.setupState === "complete") {
    return "Setup complete";
  }
  if (item.setupState === "unavailable" || !Array.isArray(item.needsSetup)) {
    return "Setup not checked yet";
  }
  return `Needs setup: ${item.needsSetup.map(needLabel).join(", ")}`;
}

export function verificationLabel(status: string): string {
  return status === "verified"
    ? "Verified"
    : status === "unverified"
      ? "Not verified yet"
      : `Verification: ${status}`;
}

export function costLabel(cost: { kind: string }): string {
  return cost.kind === "unavailable" ? "Cost not available yet" : `Cost: ${cost.kind}`;
}

export function isUnavailable(value: unknown): value is PersonalRoutineUnavailable {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "unavailable"
  );
}

export function runtimeLabel(value: unknown): string {
  return isUnavailable(value) ? "Not available yet" : String(value);
}
